import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';
import { api, thumb } from '@/lib/api';
import { CouponBox, autoApplyCoupon } from '@/components/CouponBox';
import { trackInitiateCheckout, trackAddPaymentInfo } from '@/lib/analytics';
import { toast } from 'sonner';
import {
  Lock, Loader2, ShieldCheck, ShoppingBag, Minus, Plus, Trash2, ArrowLeft,
  AlertTriangle,
} from 'lucide-react';

// Public checkout page.
// 1) Sales channels (Facebook/Instagram Shops, etc.) send shoppers to
//    /checkout?products=SKU:QTY,SKU:QTY — we resolve those into live products.
// 2) Visiting /checkout with no params checks out the shopper's saved cart.
export default function CheckoutPage() {
  const [params] = useSearchParams();
  const productsParam = params.get('products') || '';
  const cart = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const fromChannel = productsParam.trim().length > 0;
  const [lines, setLines] = useState([]);       // {product_id, name, image, price, qty, has_variants}
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(fromChannel);
  const [email, setEmail] = useState('');
  const [paying, setPaying] = useState(false);
  const [coupon, setCoupon] = useState(null); // {code, discount, message}

  const subtotalForCoupon = lines.reduce((a, l) => a + l.price * l.qty, 0);
  const discount = coupon ? Math.min(coupon.discount, subtotalForCoupon) : 0;

  // Auto-apply ?coupon=CODE from Facebook/Instagram promo links — fires once
  // the order lines are resolved so the minimum-order check uses real totals.
  const couponParam = params.get('coupon') || '';
  const couponTried = React.useRef(false);
  useEffect(() => {
    if (!couponParam || couponTried.current || lines.length === 0) return;
    couponTried.current = true;
    autoApplyCoupon(couponParam, subtotalForCoupon, setCoupon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponParam, lines.length]);

  // Channel mode: resolve SKUs from the URL
  useEffect(() => {
    if (!fromChannel) return;
    api.post('/checkout/resolve', { products: productsParam })
      .then(r => {
        setLines((r.data.items || []).map(p => ({
          product_id: p.id, sku: p.sku || null, name: p.name, image: p.image,
          price: Number(p.price || 0), qty: p.requested_qty || 1,
          has_variants: !!p.has_variants,
          variant_vid: p.variant_vid || null,
          variant_name: p.variant_name || null,
        })));
        setUnmatched(r.data.unmatched || []);
      })
      .catch(() => setUnmatched([productsParam]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsParam]);

  // Cart mode: mirror the saved cart
  useEffect(() => {
    if (fromChannel) return;
    setLines(cart.items.map(i => ({
      product_id: i.product_id, variant_vid: i.variant_vid, name: i.name,
      variant_name: i.variant_name, image: i.image, price: i.price, qty: i.qty,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromChannel, cart.items]);

  const setQty = (idx, qty) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, qty: Math.max(1, Math.min(20, qty)) } : l));
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  const subtotal = useMemo(() => lines.reduce((a, l) => a + l.price * l.qty, 0), [lines]);
  const count = useMemo(() => lines.reduce((a, l) => a + l.qty, 0), [lines]);
  const contentIds = useMemo(() => lines.map(l => l.sku || l.product_id), [lines]);

  // InitiateCheckout fires ONCE per visit, when the order review actually has
  // items (guards against refires from qty edits or re-renders).
  const icFired = React.useRef(false);
  useEffect(() => {
    if (lines.length > 0 && !icFired.current) {
      icFired.current = true;
      trackInitiateCheckout(subtotal, count, contentIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.length]);

  const pay = async () => {
    if (lines.length === 0) return;
    const guestEmail = email.trim().toLowerCase();
    if (!user && guestEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      toast.error('That email doesn\u2019t look right — fix it or leave it blank');
      return;
    }
    setPaying(true);
    trackAddPaymentInfo(subtotal, count, contentIds); // shopper hands off to Stripe payment
    try {
      const r = await api.post('/stripe/checkout', {
        purpose: 'cart',
        items: lines.map(l => ({ product_id: l.product_id, variant_vid: l.variant_vid || undefined, quantity: l.qty })),
        customer_email: user?.email || guestEmail || undefined,
        coupon: coupon?.code || undefined,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed — please try again');
      setPaying(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="checkout-page">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#17352D]">
          <ArrowLeft className="h-4 w-4" /> Keep shopping
        </Link>
        <h1 className="mt-3 font-headline text-3xl md:text-4xl text-[#17352D] flex items-center gap-3">
          <Lock className="h-6 w-6 text-[#C8922F]" /> Checkout
        </h1>
        <p className="mt-1 text-sm text-slate-600">Review your order, then pay securely with Stripe.</p>

        {unmatched.length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="checkout-unmatched">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Some items from the link are no longer available: {unmatched.join(', ')}</span>
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-slate-500"><Loader2 className="h-7 w-7 animate-spin mx-auto" /></div>
        ) : lines.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-200 p-14 text-center" data-testid="checkout-empty">
            <ShoppingBag className="h-12 w-12 mx-auto text-slate-300" />
            <div className="mt-4 font-headline text-2xl text-[#17352D]">Nothing to check out</div>
            <p className="mt-2 text-slate-600">Your cart is empty — browse the shop to add products.</p>
            <Button asChild className="mt-6 rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-11 px-6">
              <Link to="/shop" data-testid="checkout-empty-shop-btn">Browse the Shop</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="mt-6 rounded-2xl border border-[#E6E9EE] bg-white divide-y divide-[#F0F2F5]" data-testid="checkout-items">
              {lines.map((l, idx) => (
                <div key={`${l.product_id}-${l.variant_vid || ''}`} className="p-4 flex items-center gap-4" data-testid={`checkout-item-${l.product_id}`}>
                  <div className="h-16 w-16 rounded-xl overflow-hidden bg-[#F2F7F5] shrink-0 flex items-center justify-center">
                    {l.image ? <img src={thumb(l.image, 200)} alt={l.name} loading="lazy" decoding="async" className="h-full w-full object-cover" onError={(e) => { if (l.image && e.currentTarget.src !== l.image) e.currentTarget.src = l.image; }} /> : <ShoppingBag className="h-6 w-6 text-slate-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#17352D] line-clamp-2">{l.name}</div>
                    {l.variant_name && <div className="text-xs text-slate-500">Option: {l.variant_name}</div>}
                    <div className="mt-1.5 flex items-center gap-3">
                      <div className="inline-flex items-center border border-[#E6E9EE] rounded-lg overflow-hidden">
                        <button onClick={() => setQty(idx, l.qty - 1)} disabled={l.qty <= 1} className="h-8 w-8 flex items-center justify-center hover:bg-[#F2F7F5] disabled:opacity-40" data-testid={`checkout-qty-minus-${l.product_id}`} aria-label="Decrease quantity"><Minus className="h-3 w-3" /></button>
                        <div className="h-8 min-w-[2.5ch] px-1.5 flex items-center justify-center text-xs font-semibold text-[#17352D]">{l.qty}</div>
                        <button onClick={() => setQty(idx, l.qty + 1)} className="h-8 w-8 flex items-center justify-center hover:bg-[#F2F7F5]" data-testid={`checkout-qty-plus-${l.product_id}`} aria-label="Increase quantity"><Plus className="h-3 w-3" /></button>
                      </div>
                      <button onClick={() => removeLine(idx)} className="text-slate-400 hover:text-rose-600" data-testid={`checkout-remove-${l.product_id}`} aria-label={`Remove ${l.name}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="text-right font-headline text-lg text-[#17352D] whitespace-nowrap">${(l.price * l.qty).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="checkout-summary">
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>Subtotal ({count} {count === 1 ? 'item' : 'items'})</span>
                <span className="font-semibold text-[#17352D]" data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-sm text-slate-600">
                <span>Shipping</span><span className="text-emerald-700 font-semibold">FREE</span>
              </div>
              <div className="mt-3">
                <CouponBox subtotal={subtotal} coupon={coupon} onChange={setCoupon} />
              </div>
              {discount > 0 && (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-emerald-700">Discount ({coupon.code})</span>
                  <span className="font-semibold text-emerald-700" data-testid="checkout-discount">-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-[#E6E9EE] flex items-center justify-between">
                <span className="font-semibold text-[#17352D]">Total</span>
                <span className="font-headline text-2xl text-[#17352D]" data-testid="checkout-total">${(subtotal - discount).toFixed(2)}</span>
              </div>
              {!user && (
                <div className="mt-4">
                  <Label className="text-xs">Email for order updates (optional)</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" className="rounded-xl mt-1 h-10" data-testid="checkout-email-input" />
                </div>
              )}
              <Button onClick={pay} disabled={paying} className="mt-5 w-full rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12" data-testid="checkout-pay-btn">
                {paying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</> : <><Lock className="h-4 w-4 mr-2" /> Pay Securely — ${(subtotal - discount).toFixed(2)}</>}
              </Button>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-[#C8922F]" /> Secure payment powered by Stripe · 7-day returns
              </div>
            </div>
          </>
        )}
      </section>
    </Layout>
  );
}
